import { AfterContentInit, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { concatMap, count, from, map, of, reduce, toArray } from 'rxjs';
import { FileServiceService } from '../service/file-service.service';
import { FileCaptureService } from 'app/service/file-capture.service';
import { environment } from 'environments/environment';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  standalone: false,
  selector: 'app-review-files',
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './review-files.component.html',
  styleUrls: ['./review-files.component.css']
})
export class ReviewFilesComponent implements OnInit {
  files: any;
  totalFilesSize: number = 0;
  isDisabled: boolean;
  openUploadModal: boolean = false;
  uploadedFiles: File[] | null = null;
  fileSubmitted: boolean = false;
  showFileUploadError: boolean;

  constructor(
    private route: Router,
    public service: FileServiceService,
    private fcservice: FileCaptureService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private ngxspinner : NgxSpinnerService
  ) {
   
  }

ngOnInit() {
  this.service.setPageVisited('review-files');
  const allCachedFiles = this.service.getCache('files');
  if (allCachedFiles) {
    const currentRefId = this.service.getCache('referenceId');
    this.files = currentRefId
      ? allCachedFiles.filter((f: any) => !f.referenceId || f.referenceId === currentRefId)
      : allCachedFiles;
    from(this.files.map((x: any) => x.fileSize))
      .pipe(reduce((a: any, b: any) => a + b, 0))
      .subscribe(x => this.totalFilesSize = x);
    this.service.referenceFileDetails.files = this.files;
    this.uploadedFiles = this.fcservice.fileArray;

    this.isDisabled = this.files == null || this.files.length < 1;

    const attachmentSaved = this.service.getCache('attachmentSaved');
    const caseActivityKey = this.service.getCache('caseActivityKey');
    const hasInProgressFiles = attachmentSaved === 'Saved' &&
      !!caseActivityKey &&
      this.files?.some((f: any) =>
        f.attachmentKey &&
        f.scanStatus !== 1 &&
        f.scanStatus !== 99 &&
        f.uploadFailed !== true &&
        f.percentage !== 500 &&
        f.percentage !== 600
      );
    if (hasInProgressFiles) {
      this.openUploadModal = true;
    }
    }
  }

  reviewAndScan() {
    setTimeout(() => {
      const attachment = {
        referenceId: '',
        attachmentKeyList: []
      };
      attachment.referenceId = this.service.getCache('referenceId');
      attachment.attachmentKeyList = this.files.map(x => x.attachmentKey);
      if (attachment.attachmentKeyList.length > 0) {
        this.fcservice.ReviewAndScanAttachmnents(attachment).subscribe(
          (result: boolean) => {
          },
          (error: any) => {
          }
        );
      }
    }, 3000);
  }

  next() {
    this.route.navigate(['initiate-download']);
  }

  removeFile(index: number) {
    this.totalFilesSize = this.totalFilesSize - this.files[index].fileSize;
    this.files.splice(index, 1);
    this.service.setCacheForitems([{ id: "files", value: this.files }]);
    const newFiles = this.files.filter(x => x.isUpload == 1)
    if (newFiles == 0 && this.files.length > 0) {
      this.isDisabled = false;
    }
  }

  addNewFile() {
    this.route.navigate(['file-upload']);
  }

  back() {
    this.route.navigate(['file-upload']);
  }
  
  onSubmit() {
    const newFiles = this.files.filter(x => x.isUpload == 1);
    if (newFiles.length > 0) {
      this.openUploadModal = true;
      return;
    }
    this.submitFinalDocument();
  }

  handleUploadCompleted(event: any) {
    this.openUploadModal = false;

    const successfulFiles = event.files.filter((x: any) =>
      x.attachmentKey &&
      x.scanStatus === 1 &&
      x.percentage !== 500 &&
      x.percentage !== 600 &&
      x.uploadFailed !== true &&
      x.failureToUpload !== true &&
      x.scanStatus !== 99
    );

    const allFilesSucceeded =
      event.hasInvalidFiles !== true &&
      event.hasUploadErrors !== true &&
      successfulFiles.length === event.files.length;

    this.files = allFilesSucceeded ? successfulFiles : event.files;
    this.service.referenceFileDetails.files = this.files;
    this.service.setCacheForitems([{ id: 'files', value: this.files }]);

    console.log('[ReviewFiles][handleUploadCompleted] summary', {
      totalFiles: event.files.length,
      successfulFiles: successfulFiles.length,
      allFilesSucceeded,
      hasInvalidFiles: event.hasInvalidFiles,
      hasUploadErrors: event.hasUploadErrors
    });

    if (allFilesSucceeded) {
      this.showFileUploadError = false;
      this.submitFinalDocument();
      return;
    }

    this.showFileUploadError = true;
    this.isDisabled = false;
  }

  submitFinalDocument() {
    const validFiles = this.files.filter((x: any) =>
      x.attachmentKey &&
      x.scanStatus === 1 &&
      x.isVisible !== x.attachmentKey + '_99' &&
      x.uploadFailed !== true &&
      x.failureToUpload !== true &&
      x.scanStatus !== 99 &&
      x.percentage !== 500 &&
      x.percentage !== 600
    );

    const pendingOrFailedFiles = this.files.filter((x: any) =>
      !(
        x.attachmentKey &&
        x.scanStatus === 1 &&
        x.isVisible !== x.attachmentKey + '_99' &&
        x.uploadFailed !== true &&
        x.failureToUpload !== true &&
        x.scanStatus !== 99 &&
        x.percentage !== 500 &&
        x.percentage !== 600
      )
    );

    if (pendingOrFailedFiles.length > 0) {
      console.log('[ReviewFiles][submitFinalDocument] blocked-partial-submit', {
        totalFiles: this.files.length,
        validFiles: validFiles.length,
        blockedFiles: pendingOrFailedFiles.map((file: any) => ({
          fileName: file.fileName,
          attachmentKey: file.attachmentKey,
          scanStatus: file.scanStatus,
          uploadStatus: file.uploadStatus,
          uploadFailed: file.uploadFailed,
          failureToUpload: file.failureToUpload,
          percentage: file.percentage
        }))
      });
      this.showFileUploadError = true;
      this.isDisabled = false;
      return;
    }

    const submitPayloads = this.buildSubmitPayloads(validFiles);

    console.log('[ReviewFiles][submitFinalDocument] payload', {
      referenceId: this.service.referenceFileDetails.referenceId,
      submitGroupCount: submitPayloads.length,
      submitGroups: submitPayloads.map((payload: any) => ({
        caseActivityKey: payload.CaseActivityKey,
        activeAttachmentKeys: payload.ActiveAttachmentKey,
        activeAttachmentKeysJson: JSON.stringify(payload.ActiveAttachmentKey)
      })),
      validFileCount: validFiles.length,
      validFiles: validFiles.map((file: any) => ({
        fileName: file.fileName,
        attachmentKey: file.attachmentKey,
        caseActivityKey: file.caseActivityKey,
        serverCaseActivityKey: file.serverCaseActivityKey,
        scanStatus: file.scanStatus
      })),
      validFilesJson: JSON.stringify(validFiles.map((file: any) => ({
        fileName: file.fileName,
        attachmentKey: file.attachmentKey,
        caseActivityKey: file.caseActivityKey,
        serverCaseActivityKey: file.serverCaseActivityKey,
        scanStatus: file.scanStatus
      })))
    });

    if (!submitPayloads.length) {
      this.showFileUploadError = true;
      this.isDisabled = false;
      return;
    }

    this.ngxspinner.show();
    from(submitPayloads).pipe(
      concatMap((payload: any) =>
        this.fcservice.submitDocument(payload).pipe(
          map((res: boolean) => ({ payload, res }))
        )
      ),
      toArray()
    ).subscribe(
      (results: any[]) => {
        const allSucceeded = results.every((result: any) => result.res === true);

        console.log('[ReviewFiles][submitFinalDocument] grouped-results', {
          allSucceeded,
          results: results.map((result: any) => ({
            caseActivityKey: result.payload.CaseActivityKey,
            activeAttachmentKeys: result.payload.ActiveAttachmentKey,
            response: result.res
          }))
        });

        this.fileSubmitted = allSucceeded;
        this.ngxspinner.hide();

        if (!allSucceeded) {
          this.showFileUploadError = true;
          this.isDisabled = false;
          return;
        }

        const sessionId = window.sessionStorage.getItem(this.fcservice.SESSION_KEY) || '';
        const payorKey = Number(window.sessionStorage.getItem(this.fcservice.PAYOR_KEY) || 0);
        const referenceId = this.service.referenceFileDetails.referenceId;
        this.fcservice.releaseActiveReferenceLock(payorKey, referenceId, sessionId).subscribe();

        this.service.setCacheForitems([
          { id: 'attachmentSaved', value: null },
          { id: 'caseActivityKey', value: null },
          { id: 'attachmentSavedForRef', value: null },
          { id: 'files', value: null }
        ]);

        this.fcservice.fileArray = [];
        this.route.navigate(['confirmation']);
      },
      () => {
        this.ngxspinner.hide();
        this.showFileUploadError = true;
        this.isDisabled = false;
      }
    );
  }

  private buildSubmitPayloads(validFiles: any[]): any[] {
    const groupedFiles = new Map<any, any[]>();

    validFiles.forEach((file: any) => {
      const groupKey = file.serverCaseActivityKey || file.caseActivityKey || this.service.referenceFileDetails.caseActivityKey;
      const existingFiles = groupedFiles.get(groupKey) || [];
      existingFiles.push(file);
      groupedFiles.set(groupKey, existingFiles);
    });

    return Array.from(groupedFiles.entries()).map(([caseActivityKey, files]) => ({
      BusinessName: this.service.referenceFileDetails.businessName,
      ContactName: this.service.referenceFileDetails.contactName,
      ContactPhone: this.service.referenceFileDetails.contactPhone,
      ContactEmail: this.service.referenceFileDetails.contactEmail,
      ReferenceId: this.service.referenceFileDetails.referenceId,
      CaseActivityKey: caseActivityKey,
      ActiveAttachmentKey: files.map((file: any) => file.attachmentKey),
      TimeZoneUtcDifference: new Date().getTimezoneOffset()
    }));
  }
  closeUploadPopup() {
    this.openUploadModal = false;
  }
}
